import { ReactNode } from 'react';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { Document } from '@contentful/rich-text-types';
import { hasRichTextContent } from 'helpers/content/hasRichTextContent';
import RichText from '../../../../atoms/rich-text';

export type ProductDescription = {
  /** Rendered description, or `undefined` when there is nothing to show and the section is dropped. */
  node?: ReactNode;
  /** Same copy as plain text, for meta tags and JSON-LD. */
  plainText?: string;
};

/**
 * Resolves the PDP description from Contentful first, then the commercetools string.
 *
 * Both the visible node and the plain-text copy come from here so they cannot disagree. They used
 * to be derived separately from the same condition; when the visible side started gating on
 * "renders anything" and the plain-text side kept gating on truthiness, a product with a cleared
 * Contentful description but real commercetools copy rendered that copy while handing JSON-LD an
 * empty string — structured data contradicting the page.
 */
export const getProductDescription = (
  contentDescription: Document | undefined,
  commercetoolsDescription: string | undefined,
): ProductDescription => {
  if (hasRichTextContent(contentDescription)) {
    return {
      node: <RichText content={contentDescription} variant="small" className="text-tertiary" />,
      plainText: documentToPlainTextString(contentDescription),
    };
  }

  if (commercetoolsDescription?.trim()) {
    return {
      node: (
        <p className="text-14 font-regular leading-loose tracking-normal text-tertiary [&_ul]:p-0 [&_ul]:[list-style:inside_circle]">
          {commercetoolsDescription}
        </p>
      ),
      plainText: commercetoolsDescription,
    };
  }

  return { node: undefined, plainText: undefined };
};
